import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { flows, isApiError } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Badge } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';

/**
 * Facebook's SDK, as this file uses it.
 *
 * `window.FB` is already declared globally by the WhatsApp connect, whose login
 * asks for a `code` (Embedded Signup) rather than an access token. Redeclaring
 * the global here would collide with it, and widening theirs is not this
 * change's to do, so the handle is read through one narrow cast and typed
 * locally. One cast in one place beats two incompatible global declarations.
 */
interface MetaFacebookSdk {
  init(options: { appId: string; autoLogAppEvents?: boolean; xfbml?: boolean; version: string }): void;
  login(
    callback: (response: { authResponse?: { accessToken?: string } | null; status?: string }) => void,
    options?: Record<string, unknown>
  ): void;
}

/** The SDK handle, or undefined before the script has loaded. */
function fbHandle(): MetaFacebookSdk | undefined {
  return (window as unknown as { FB?: MetaFacebookSdk }).FB;
}

const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
const SDK_ELEMENT_ID = 'rr-facebook-jssdk';

/**
 * Connecting a Facebook page, and the Instagram account behind it.
 *
 * Three steps, in this order, because each one can only fail in ways the
 * previous one has already ruled out:
 *
 *   1. Ask the platform whether this is switched on **before** loading
 *      Facebook's SDK. Loading it against an app that cannot do this produces
 *      "This app needs at least one supported permission", which tells a
 *      customer nothing and sends them looking in their own settings for a
 *      fault that is ours. The WhatsApp connect learnt this the hard way on
 *      7 September 2026; this is the same preflight.
 *   2. Sign in to Facebook, hand the short-lived token to the server, and get
 *      back what this person actually administers. The exchange for a
 *      long-lived token happens on the server, where the app secret is.
 *   3. Choose a page. Only then is anything stored.
 */
export function ConnectMeta({
  connected,
  onDone
}: {
  connected: flows.MetaAccount[];
  onDone: () => void;
}) {
  const t = useT();
  const [pages, setPages] = useState<flows.MetaConnectablePage[] | null>(null);
  const [userToken, setUserToken] = useState<string | null>(null);
  const [sdkError, setSdkError] = useState<string | null>(null);

  // Step 1, on mount: is this even possible?
  const info = useQuery({
    queryKey: ['meta-connect-info'],
    queryFn: () => flows.getMetaConnectInfo(),
    staleTime: 60_000
  });
  const available = info.data?.data.available === true;

  // Step 2.
  const listPages = useMutation({
    mutationFn: (token: string) => flows.listConnectableMetaPages(token),
    onSuccess: (res) => {
      setUserToken(res.data.user_access_token);
      setPages(res.data.pages);
    },
    onError: (e) => notifyError(message(e, t), t('channels.connect'))
  });

  // Step 3.
  const connect = useMutation({
    mutationFn: (req: flows.ConnectMetaAccountRequest) => flows.connectMetaAccount(req),
    onSuccess: (res) => {
      notifySuccess(t('channels.connected', { name: res.data.label }), t('nav.channels'));
      onDone();
    },
    onError: (e) => notifyError(message(e, t), t('channels.connect'))
  });

  async function signIn() {
    const cfg = info.data?.data;
    if (!cfg?.app_id || !cfg.graph_version) return;
    setSdkError(null);
    try {
      const fb = await loadFacebookSdk(cfg.app_id, cfg.graph_version);
      fb.login(
        (response) => {
          const token = response.authResponse?.accessToken;
          if (!token) {
            // A cancelled dialog is not a fault, and must not read like one.
            if (response.status !== 'unknown') setSdkError(t('channels.sdk.noToken'));
            return;
          }
          listPages.mutate(token);
        },
        // A Business app grants page messaging through a Facebook Login for Business configuration, whose id
        // replaces the list of scopes (8 October 2026). Without one, the scopes are asked for by name.
        cfg.config_id
          ? { config_id: cfg.config_id }
          : { scope: (cfg.scopes ?? []).join(','), return_scopes: true }
      );
    } catch (e) {
      setSdkError(
        e instanceof Error && e.message === 'sdk-blocked'
          ? t('channels.sdk.blocked')
          : t('channels.sdk.unavailable')
      );
    }
  }

  if (info.isPending) return <Text role="bodySmall">{t('common.loading')}</Text>;

  // The platform's problem, said in one sentence, with nothing for the
  // customer to do about it. The real reason is in our logs, not on their
  // screen.
  if (!available) {
    return (
      <Alert color="yellow" title={t('channels.unavailable.title')}>
        {info.data?.data.reason ?? t('channels.unavailable.body')}
      </Alert>
    );
  }

  return (
    <Stack gap="lg" style={{ padding: 4 }}>
      {!pages && (
        <Stack gap="sm">
          <Text role="bodySmall">{t('channels.connect.intro')}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('channels.connect.needs')}</Text>
          {sdkError && <Alert color="red" title={t('channels.sdk.title')}>{sdkError}</Alert>}
          <Group>
            <Button onClick={signIn} loading={listPages.isPending}>
              {t('channels.connect.signIn')}
            </Button>
          </Group>
        </Stack>
      )}

      {pages && pages.length === 0 && (
        <Alert color="yellow" title={t('channels.noPages.title')}>{t('channels.noPages.body')}</Alert>
      )}

      {pages && pages.map((p) => {
        const already = (channel: string) =>
          connected.some((a) => a.page_id === p.id && a.channel === channel && a.is_active);
        return (
          <Card withBorder key={p.id}>
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={2}>
                  <Text role="body" fw={600}>{p.name}</Text>
                  {p.category && <Text role="caption" c="var(--rr-color-text-muted)">{p.category}</Text>}
                </Stack>
                {already('messenger')
                  ? <Badge size="sm" color="green" variant="light">{t('channels.alreadyConnected')}</Badge>
                  : (
                    <Button
                      size="xs"
                      disabled={!p.can_message || !p.can_subscribe}
                      loading={connect.isPending && connect.variables?.page_id === p.id && connect.variables?.channel === 'messenger'}
                      onClick={() => userToken && connect.mutate({
                        channel: 'messenger',
                        page_id: p.id,
                        user_access_token: userToken
                      })}
                    >
                      {t('channels.connectMessenger')}
                    </Button>
                  )}
              </Group>

              {/*
                What Meta will refuse before it is tried. `tasks` on the page
                says whether this person may message from it and whether they
                may switch delivery on; without both, connecting would succeed
                on our side and fail on Meta's, later, with a sentence naming
                nothing they can act on.
              */}
              {!p.can_message && (
                <Text role="caption" c="var(--rr-color-danger)">{t('channels.cannotMessage')}</Text>
              )}
              {p.can_message && !p.can_subscribe && (
                <Text role="caption" c="var(--rr-color-danger)">{t('channels.cannotSubscribe')}</Text>
              )}

              {/* Instagram, through this page. */}
              <Group justify="space-between" align="center" wrap="nowrap" className="rr-channels__ig">
                <Stack gap={2}>
                  <Text role="bodySmall" fw={500}>
                    {p.instagram_username ? `@${p.instagram_username}` : t('channels.instagram')}
                  </Text>
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {p.instagram_id
                      ? t('channels.instagram.ready')
                      : p.instagram_needs_upgrade
                        ? t('channels.instagram.personal')
                        : t('channels.instagram.none')}
                  </Text>
                </Stack>
                {p.instagram_id && (already('instagram')
                  ? <Badge size="sm" color="green" variant="light">{t('channels.alreadyConnected')}</Badge>
                  : (
                    <Button
                      size="xs"
                      variant="default"
                      disabled={!p.can_message || !p.can_subscribe}
                      loading={connect.isPending && connect.variables?.page_id === p.id && connect.variables?.channel === 'instagram'}
                      onClick={() => userToken && connect.mutate({
                        channel: 'instagram',
                        page_id: p.id,
                        user_access_token: userToken
                      })}
                    >
                      {t('channels.connectInstagram')}
                    </Button>
                  ))}
              </Group>
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}

/**
 * What to show when a connect step fails.
 *
 * A refusal from this platform already carries a sentence written for the
 * person reading it (`MetaMessagingRefusals` translates every one of Meta's),
 * so it is shown verbatim. Anything else falls back to the generic line rather
 * than putting a stack trace on a customer's screen.
 */
function message(e: unknown, t: (k: string) => string): string {
  if (isApiError(e)) {
    const body = e.body as { error?: { message?: string } } | undefined;
    return body?.error?.message ?? e.message;
  }
  return e instanceof Error ? e.message : t('common.unknownError');
}

/**
 * Load Facebook's JS SDK once and initialise it against the app the platform
 * named. Rejects when the script cannot be fetched, which is what happens
 * behind a content blocker or on an offline machine. Same helper as the
 * WhatsApp connect, kept separate rather than shared because that file is
 * channel-wa's and this one is not.
 */
function loadFacebookSdk(appId: string, version: string): Promise<MetaFacebookSdk> {
  return new Promise((resolve, reject) => {
    const loaded = fbHandle();
    if (loaded) {
      loaded.init({ appId, autoLogAppEvents: true, xfbml: true, version });
      resolve(loaded);
      return;
    }

    const existing = document.getElementById(SDK_ELEMENT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');

    (window as unknown as { fbAsyncInit?: () => void }).fbAsyncInit = () => {
      const fb = fbHandle();
      if (!fb) { reject(new Error('sdk-missing')); return; }
      fb.init({ appId, autoLogAppEvents: true, xfbml: true, version });
      resolve(fb);
    };
    script.onerror = () => reject(new Error('sdk-blocked'));

    if (!existing) {
      script.id = SDK_ELEMENT_ID;
      script.src = SDK_SRC;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    }

    // A blocked script sometimes neither loads nor errors. Fail on a timer so
    // the button stops spinning and says something.
    window.setTimeout(() => reject(new Error('sdk-timeout')), 15_000);
  });
}
